import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { ExternalLinkIcon } from 'assets/icons';
import { CopyReferenceWithFeedback, CopySelectionWrapper } from 'components/CopyReferenceFeedback';
import { DocumentationAlertButton } from 'components/DocumentationAlertButton';
import { getLastSearchedVehicleKey, getSearchVehicleResult } from 'domains/catalog/Catalog.store';
import { DHReferenceLocal, setSelectedTab as setSelectedTabMenu } from 'domains/references';
import { SparePartsViewType } from 'domains/user';
import { theme } from 'styles';
import { Flex, Icon, LinkRoundButton, MarginBox, Text } from 'UI';
import { imageSrcSwitch, URL } from 'UI/Image/Image';
import {
  CloseButton,
  ExpandedContainer,
  ExpandedGalleryColumn,
  ExpandedInfoColumn,
  OpenFullImageButton,
} from './ProductExpandedImageView.styled';
import { ProductGalleryState } from './ProductGallery.types';
import { ProductImageGallery } from './ProductImageGallery';

interface ProductExpandedImageViewProps {
  gallery: ProductGalleryState;
  isDiscontinued?: boolean;
  refLocal: DHReferenceLocal;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  sparePartsView: SparePartsViewType;
  onClose: () => void;
  onOpenExpanded: (index: number) => void;
}

export const ProductExpandedImageView = ({
  gallery,
  isDiscontinued,
  refLocal,
  selectedIndex,
  setSelectedIndex,
  sparePartsView,
  onClose,
  onOpenExpanded,
}: ProductExpandedImageViewProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedAsset = gallery.assets[selectedIndex];
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const vehicle = useSelector((state: RootState) => getSearchVehicleResult(state, vehicleKey));
  const vehicleDetail = vehicle?.vehicleDetail;
  const displaySupersessionLink =
    refLocal.supersessionChainReferences && refLocal.supersessionChainReferences.length > 0;
  const canOpenFullImage = selectedAsset?.type === 'image' && !!selectedAsset.imageKey;
  const is3dExpanded = selectedAsset?.type === '3d';

  const handleOpenFullImage = () => {
    if (!canOpenFullImage) {
      return;
    }
    const imageUrl = imageSrcSwitch(URL, selectedAsset.imageKey);
    if (imageUrl) {
      window.open(imageUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSupersessionClick = () => {
    dispatch(setSelectedTabMenu('substitute_references'));
    onClose();
  };

  return (
    <ExpandedContainer data-testid={'product-expanded-image-view'} $viewerOnly={is3dExpanded}>
      <CloseButton
        onClick={onClose}
        aria-label={t('catalog.product.image.close_expanded_view', 'Close expanded image view')}
      />
      <ExpandedGalleryColumn $viewerOnly={is3dExpanded}>
        <ProductImageGallery
          gallery={gallery}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          sparePartsView={sparePartsView}
          isDiscontinued={isDiscontinued}
          expanded
          hideThumbnails
          onOpenExpanded={onOpenExpanded}
        />
      </ExpandedGalleryColumn>
      {!is3dExpanded && (
        <ExpandedInfoColumn direction={'column'}>
          <Flex align={'center'} size={0}>
            <CopySelectionWrapper referenceNumber={refLocal.referenceNumber}>
              <Text type={'light_14_black_45'} data-cy={'expanded-reference-number'}>
                {refLocal.referenceNumber}
              </Text>
            </CopySelectionWrapper>
            <MarginBox mr={8} />
            <CopyReferenceWithFeedback referenceNumber={refLocal.referenceNumber} />
            {(refLocal.brand === 'MOTRIO' || refLocal.brand === 'MOTRIO_PNEU') && (
              <>
                <MarginBox mr={8} />
                <DocumentationAlertButton
                  reference={refLocal}
                  vehicleDetail={vehicleDetail}
                  vehicleRegistrationNumber={vehicleDetail?.vrn ?? vehicleDetail?.vehicleKey}
                  inverted
                  size={12}
                  boxSize={'xsm'}
                />
              </>
            )}
            {displaySupersessionLink && (
              <>
                <MarginBox mr={8} />
                <LinkRoundButton onClick={handleSupersessionClick} />
              </>
            )}
          </Flex>
          <MarginBox mt={10} />
          <Text type={'h1_banner_light'} disableGutter>
            {refLocal.name}
          </Text>
          {refLocal.commercialName && (
            <>
              <MarginBox mt={14} />
              <Text type={'h6_light'} disableGutter>
                {refLocal.commercialName}
              </Text>
            </>
          )}
          <ProductImageGallery
            gallery={gallery}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            sparePartsView={sparePartsView}
            isDiscontinued={isDiscontinued}
            thumbnailsVertical
            thumbnailsOnly
            hideThumbnails={false}
            onOpenExpanded={onOpenExpanded}
          />
          <MarginBox mt={30} />
          {canOpenFullImage && (
            <Flex align={'flex-end'}>
              <OpenFullImageButton type={'button'} onClick={handleOpenFullImage}>
                <Icon IconComponent={ExternalLinkIcon} size={16} color={theme.color.clear_blue} />
                {t('catalog.product.image.open_full_image', 'Open full image')}
              </OpenFullImageButton>
            </Flex>
          )}
        </ExpandedInfoColumn>
      )}
    </ExpandedContainer>
  );
};
